import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import BlogContent from '../components/storefront/BlogContent';
import { Icon } from '../components/ui/Icon';
import api from '../utils/api';

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

export default function PressPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [more, setMore] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setNotFound(false); setPost(null);
    window.scrollTo({ top: 0 });
    api.get(`/blog/${slug}`)
      .then(res => { if (active) setPost(res.data); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    api.get('/blog', { params: { limit: 4 } })
      .then(res => { if (active) setMore((res.data || []).filter(p => p.slug !== slug).slice(0, 3)); })
      .catch(() => {});
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 grid place-items-center text-gray-400 py-32">Loading story…</div>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 grid place-items-center py-32 text-center px-4">
          <div>
            <Icon name="search" className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h1 className="font-display text-3xl text-gray-800">Story not found</h1>
            <p className="text-gray-400 mt-2 mb-6">This press piece may have moved or been unpublished.</p>
            <Link to="/press" className="inline-flex items-center gap-2 rounded-full bg-tea-600 text-white font-semibold px-6 py-3 hover:bg-tea-700 transition-colors">
              ← Back to The Press
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* ── Cover hero ──────────────────────────────────────────────────────── */}
      <header className="relative">
        {post.coverImage ? (
          <div className="relative h-[42vh] md:h-[60vh] min-h-[18rem] overflow-hidden">
            <img src={post.coverImage} alt={post.coverImageAlt || post.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 text-white">
              {post.category && (
                <span className="inline-block rounded-full bg-tea-600 text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 mb-4">
                  {post.category}
                </span>
              )}
              <h1 className="font-display text-3xl md:text-5xl font-extrabold leading-tight drop-shadow">{post.title}</h1>
            </div>
          </div>
        ) : (
          <div className="relative polka-pink border-b border-tea-100">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
              {post.category && (
                <span className="inline-block rounded-full bg-tea-600 text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 mb-5">
                  {post.category}
                </span>
              )}
              <h1 className="font-display text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">{post.title}</h1>
            </div>
          </div>
        )}
      </header>

      <article className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          {/* Byline */}
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-gray-400 mb-3">
            {post.author && <span className="font-semibold text-tea-700">{post.author}</span>}
            {post.author && <span className="w-1 h-1 rounded-full bg-tea-300" />}
            <span>{post.publishedAt ? fmtDate(post.publishedAt) : fmtDate(post.createdAt)}</span>
            <span className="w-1 h-1 rounded-full bg-tea-300" />
            <span>{post.readMinutes || 1} min read</span>
          </div>

          {post.excerpt && (
            <p className="text-center font-display text-xl md:text-2xl text-gray-500 italic leading-relaxed mb-8 max-w-2xl mx-auto">
              {post.excerpt}
            </p>
          )}

          {/* Polka divider */}
          <div className="flex items-center justify-center gap-2.5 mb-10" aria-hidden="true">
            <span className="w-2.5 h-2.5 rounded-full bg-tea-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-tea-300" />
            <span className="w-2.5 h-2.5 rounded-full bg-tea-400" />
          </div>

          {/* Body */}
          <BlogContent body={post.body} />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-6 border-t border-dashed border-tea-100 flex flex-wrap gap-2">
              {post.tags.map(t => (
                <Link key={t} to={`/press?tag=${encodeURIComponent(t)}`}
                  className="text-xs font-semibold rounded-full bg-tea-50 text-tea-700 px-3 py-1.5 hover:bg-tea-100 transition-colors">
                  #{t}
                </Link>
              ))}
            </div>
          )}

          <div className="mt-10 text-center">
            <Link to="/press" className="inline-flex items-center gap-2 text-tea-700 font-semibold hover:text-tea-900 transition-colors">
              ← All Press Stories
            </Link>
          </div>
        </div>

        {/* More to read */}
        {more.length > 0 && (
          <section className="polka-pink border-t border-tea-100 py-14">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-display text-2xl font-bold text-gray-900 text-center mb-8">More from the Press</h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {more.map(p => (
                  <Link key={p.id} to={`/press/${p.slug}`}
                    className="group bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-[16/10] overflow-hidden">
                      {p.coverImage ? (
                        <img src={p.coverImage} alt={p.coverImageAlt || p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-pink-100 to-fuchsia-100 polka-pink-dense grid place-items-center">
                          <span className="font-display text-xl text-tea-300">AB8 Press</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-display text-lg font-bold text-gray-900 leading-snug group-hover:text-tea-700 transition-colors">{p.title}</h3>
                      <div className="mt-2 text-[11px] text-gray-400">{p.readMinutes || 1} min read</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>

      <Footer />
    </div>
  );
}
